import { normalizeDomain } from "app/pages/digital-marketing/utils/validation/url/domainNormalization";

const splitLines = (value?: string): string[] =>
    value
        ? value
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean)
        : [];

/** Falls back to the raw value so normalization can never blank out what the user typed. */
const toCanonicalDomain = (domain?: string): string => normalizeDomain(domain ?? "") || (domain ?? "");

/** Same fallback per entry, so a stored domain we cannot parse survives a load-and-save round trip. */
const toCanonicalDomains = (domains: readonly string[]): string[] => {
    const seenDomains = new Set<string>();

    return domains.reduce<string[]>((canonicalDomains, domain) => {
        const canonicalDomain = toCanonicalDomain(domain);

        if (canonicalDomain && !seenDomains.has(canonicalDomain)) {
            seenDomains.add(canonicalDomain);
            canonicalDomains.push(canonicalDomain);
        }

        return canonicalDomains;
    }, []);
};

export function trademarkConvertFromFormData<
    T extends { domain?: string; whiteListedDomains?: string; variationsAndTypos?: string },
>(
    formData: T,
): Omit<T, "domain" | "whiteListedDomains" | "variationsAndTypos"> & {
    domain: string;
    whiteListedDomains: string[];
    variationsAndTypos: string[];
} {
    return {
        ...formData,
        domain: toCanonicalDomain(formData.domain),
        whiteListedDomains: toCanonicalDomains(splitLines(formData.whiteListedDomains)),
        variationsAndTypos: splitLines(formData.variationsAndTypos),
    };
}

export function trademarkConvertToFormData<
    T extends { domain?: string; whiteListedDomains?: string[]; variationsAndTypos?: string[] },
>(
    data?: T,
): Omit<T, "domain" | "whiteListedDomains" | "variationsAndTypos"> & {
    domain: string;
    whiteListedDomains: string;
    variationsAndTypos: string;
} {
    return {
        ...data,
        domain: toCanonicalDomain(data?.domain),
        whiteListedDomains: toCanonicalDomains(data?.whiteListedDomains ?? []).join("\n"),
        variationsAndTypos: data?.variationsAndTypos ? data.variationsAndTypos.join("\n") : "",
    };
}
